import { useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import plur from 'plur';
import { useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import {
  ordwayPurchaseFailedError,
  ordwayPurchaseFailure,
  ordwayPurchaseIndeterminateError,
  ordwayPurchaseMayHaveStarted,
  parseLegacyOrdwayPurchaseOutcome,
  parseOrdwayPurchaseConflict,
  reportIndeterminateOrdwayPurchase,
  type OrdwayPurchaseConflict,
  type OrdwayPurchaseFailure,
  type OrdwayPurchaseRail,
} from '@/services/billing/ordway/credits-purchase';
import { formatNumber } from '@/utils/number';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import type { PollTask } from '@/utils/poll-task';
import { invalidateCreditBalances } from '../utils/credit-balance-queries';
import type { CreditsPurchaseConfig } from '../utils/credits-purchase-config';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';

const FAILURE_MESSAGE = "Your purchase couldn't be completed. Try again or contact support.";

/** What one submit buys, in the units the analytics funnel already reports. */
export type OrdwayPurchaseAttempt = {
  /** The value posted to the purchase endpoint: credit packs for AI credits, previews for a pack. */
  quantity: number;
  totalPriceCents: number;
  totalCredits: number;
};

export type UseOrdwayCreditsPurchaseOptions = {
  config: CreditsPurchaseConfig;
  /** The surface that opened the form, reported with every event it fires. */
  pageName: CreditsPurchasePageName;
  rail: OrdwayPurchaseRail;
  /** Starts the purchase; resolves once the workflow is running, carrying its `run_id`. */
  startPurchase: (quantity: number) => Promise<{ run_id?: string; status?: string }>;
  /** The rail's status poll. Cancelled on unmount, so a late outcome is never reported. */
  poll: PollTask<[string], boolean>;
  /**
   * The quantity the form starts on. Seeds the `quantityChanged` dedupe, so leaving the preselected
   * value untouched reports no change.
   */
  initialQuantity: number;
  onSuccess: (info: { savedCard: boolean }) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
};

export type OrdwayCreditsPurchase = {
  isSubmitting: boolean;
  /**
   * Set when an attempt ended without ruling out a charge. Locking submit (while leaving Cancel and
   * dismissal alone) makes reopening the form the only way to buy more.
   */
  isChargeUnverified: boolean;
  /** Why submit is blocked by an unverified charge, or `''` when it is not. */
  chargeUnverifiedMessage: string;
  /** Runs one purchase attempt to a reported outcome. Safe to call only when submit is enabled. */
  submit: (attempt: OrdwayPurchaseAttempt) => Promise<void>;
  /** Fires the shared `quantityChanged` funnel event, at most once per distinct quantity. */
  trackQuantityChanged: (attempt: OrdwayPurchaseAttempt) => void;
};

/**
 * The purchase run behind every Ordway credits form: the analytics funnel, the snackbars, and the
 * outcome handling that decides what a customer is told and whether buying stays open.
 *
 * Ordway bills through the account's Ordway contract rather than a Stripe customer, so no form on this
 * path collects card details — the endpoint charges the card on file. The charge outlives the request:
 * the endpoint resolves once the purchase workflow is running and reports no outcome, so this polls the
 * returned `run_id` to a terminal outcome before reporting anything — staying submitted throughout,
 * which keeps the hosting modal or drawer non-dismissable and blocks a duplicate purchase. Success is
 * reported only for a confirmed terminal success; a decline, a failure, and a poll that never
 * terminates are each surfaced distinctly, and none of them tells a customer whose card may already
 * have been charged to retry.
 *
 * Every rule here is a charge-safety rule, which is why the AI-credits and Inbox-Previews forms share
 * this rather than each keeping their own copy: the only differences between the two rails are their
 * endpoints and their nouns, both supplied by the caller.
 */
export function useOrdwayCreditsPurchase({
  config,
  pageName,
  rail,
  startPurchase,
  poll,
  initialQuantity,
  onSuccess,
  onProcessingChange,
}: UseOrdwayCreditsPurchaseOptions): OrdwayCreditsPurchase {
  const { account } = useAuth();
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChargeUnverified, setIsChargeUnverified] = useState(false);

  const inProgressSnackbarId = `${config.snackbarPrefix}.purchase.inProgress`;
  const conflictSnackbarId = `${config.snackbarPrefix}.purchase.conflict`;

  // Values the mount/unmount effect needs but must not depend on: reading them through refs lets it
  // fire exactly once, rather than re-running on identities that change every render.
  const trackRef = useRef(track);
  trackRef.current = track;
  const teardownRef = useRef({ poll, snackbars, inProgressSnackbarId, conflictSnackbarId });
  teardownRef.current = { poll, snackbars, inProgressSnackbarId, conflictSnackbarId };
  const purchaseCompletedRef = useRef(false);
  const lastTrackedQuantityRef = useRef(initialQuantity);

  const creditType = config.creditType;
  const accountId = account.id;

  useEffect(() => {
    void trackRef.current('creditsPurchase', 'modalOpened', {
      'Credit Type': creditType,
      'Account Id': accountId,
      'Billing System': 'ordway',
      page_name: pageName,
    });
    return () => {
      const teardown = teardownRef.current;
      // Abandon an in-flight status poll: `perform` then resolves `undefined`, so a purchase that
      // settles after the form closed is never reported against a form that no longer exists.
      teardown.poll.cancelAll();
      teardown.snackbars.destroy(teardown.inProgressSnackbarId);
      teardown.snackbars.destroy(teardown.conflictSnackbarId);
      if (!purchaseCompletedRef.current) {
        void trackRef.current('creditsPurchase', 'modalClosed', {
          'Credit Type': creditType,
          'Billing System': 'ordway',
          page_name: pageName,
        });
      }
    };
    // Fire once on mount/unmount. `creditType` and `accountId` are constant for a mounted form, and
    // everything else it touches is read through a ref.
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount-only analytics
  }, []);

  const trackQuantityChanged = (attempt: OrdwayPurchaseAttempt) => {
    if (attempt.quantity === lastTrackedQuantityRef.current) {
      return;
    }
    lastTrackedQuantityRef.current = attempt.quantity;
    void track('creditsPurchase', 'quantityChanged', {
      'Credit Type': config.creditType,
      Quantity: attempt.quantity,
      'Total Price Cents': attempt.totalPriceCents,
      'Billing System': 'ordway',
      page_name: pageName,
    });
  };

  // fallow-ignore-next-line complexity -- one purchase attempt's outcome tree; every branch is a distinct charge-safety case
  const submit = async (attempt: OrdwayPurchaseAttempt) => {
    const context: PurchaseRunContext = {
      config,
      pageName,
      rail,
      attempt,
      accountId: account.id,
      snackbars,
      queryClient,
      track,
      poll,
      inProgressSnackbarId,
      conflictSnackbarId,
      purchaseCompletedRef,
      setIsChargeUnverified,
      onSuccess,
    };

    void track('creditsPurchase', 'purchaseSubmitted', {
      ...purchaseEventPayload(context),
      'Tax Amount Cents': 0,
      'Total With Tax Cents': attempt.totalPriceCents,
      'Total Credits': attempt.totalCredits,
    });

    setIsSubmitting(true);
    onProcessingChange?.(true);
    snackbars.create({
      variant: 'default',
      id: inProgressSnackbarId,
      message: 'Processing your purchase…',
      autoCloseTimeout: 0,
    });

    try {
      const response = await startPurchase(attempt.quantity);
      const runId = response.run_id;
      if (isEmptyString(runId)) {
        await settleWithoutRun(context, response);
        return;
      }
      const { confirmed, error } = await pollToTerminalOutcome(context, runId);
      if (confirmed) {
        reportSuccess(context);
      } else if (error !== undefined) {
        reportFailure(context, 'polling_failed', error);
        // Polling can stop before the purchase does, so the credits may have landed since. Re-read the
        // balances the copy tells the customer to check — otherwise the page keeps showing the old
        // number until a reload.
        await invalidateCreditBalances(queryClient);
      }
    } catch (error) {
      const conflict = parseOrdwayPurchaseConflict(error, rail);
      if (conflict != null) {
        trackFailure(context, 'in_progress_conflict');
        await handleConflict(context, conflict);
      } else {
        await settleThrownPurchase(context, error);
      }
    } finally {
      snackbars.destroy(inProgressSnackbarId);
      setIsSubmitting(false);
      onProcessingChange?.(false);
    }
  };

  return {
    isSubmitting,
    isChargeUnverified,
    chargeUnverifiedMessage: isChargeUnverified
      ? `Check your ${rail.balanceLabel} before buying more ${config.creditsUnitLabel ?? 'credits'}.`
      : '',
    submit,
    trackQuantityChanged,
  };
}

/**
 * Everything one purchase attempt needs to report itself. Passed to the settle helpers below so each
 * one stays a named case rather than a closure buried in the submit handler.
 */
type PurchaseRunContext = {
  config: CreditsPurchaseConfig;
  pageName: CreditsPurchasePageName;
  rail: OrdwayPurchaseRail;
  attempt: OrdwayPurchaseAttempt;
  accountId: number | string;
  snackbars: ReturnType<typeof useSnackbars>;
  queryClient: QueryClient;
  track: ReturnType<typeof useAnalytics>['track'];
  poll: PollTask<[string], boolean>;
  inProgressSnackbarId: string;
  conflictSnackbarId: string;
  purchaseCompletedRef: { current: boolean };
  setIsChargeUnverified: (locked: boolean) => void;
  onSuccess: (info: { savedCard: boolean }) => void;
};

/** The fields every purchase funnel event for this attempt carries. */
function purchaseEventPayload(context: PurchaseRunContext) {
  return {
    'Credit Type': context.config.creditType,
    'Account Id': context.accountId,
    Quantity: context.attempt.quantity,
    'Total Price Cents': context.attempt.totalPriceCents,
    'Billing System': 'ordway' as const,
    page_name: context.pageName,
  };
}

function trackFailure(context: PurchaseRunContext, errorType: string) {
  void context.track('creditsPurchase', 'purchaseFailed', {
    ...purchaseEventPayload(context),
    'Error Type': errorType,
  });
}

function reportFailure(context: PurchaseRunContext, errorType: string, error?: unknown) {
  trackFailure(context, errorType);
  const failure = ordwayPurchaseFailure(error);
  if (leavesChargeUnverified(failure)) {
    context.setIsChargeUnverified(true);
  }
  context.snackbars.create({
    variant: failure === 'indeterminate' ? 'default' : 'critical',
    id: `${context.config.snackbarPrefix}.purchase.error`,
    message: failureMessage(error),
  });
}

function reportSuccess(context: PurchaseRunContext) {
  const { attempt, config } = context;
  context.purchaseCompletedRef.current = true;
  void context.track('creditsPurchase', 'purchaseSucceeded', {
    ...purchaseEventPayload(context),
    'Tax Amount Cents': 0,
    'Total With Tax Cents': attempt.totalPriceCents,
    'Total Credits': attempt.totalCredits,
  });
  context.snackbars.create({
    variant: 'default',
    id: `${config.snackbarPrefix}.purchase.success`,
    message: config.formatSuccessMessage(attempt.totalPriceCents, attempt.totalCredits),
  });
  context.onSuccess({ savedCard: false });
}

/**
 * Waits for `runId` to reach a terminal state. `confirmed` is true only for an observed terminal
 * success. A cancelled poll (the form unmounted) reports neither a success nor an error; any other
 * ending carries its error, which the caller phrases for the run it was waiting on — the poll's own
 * copy speaks in the first person and only fits the caller's own purchase.
 */
async function pollToTerminalOutcome(
  context: PurchaseRunContext,
  runId: string,
): Promise<{ confirmed: boolean; error?: unknown }> {
  try {
    return { confirmed: (await context.poll.perform(runId)) === true };
  } catch (error) {
    return { confirmed: false, error };
  }
}

/**
 * A 409 means the purchase was refused because another one is already running for the account, so
 * nothing was charged for this attempt. Wait that run out with submit still disabled rather than
 * offering a retry that would only conflict again — and never attribute its outcome to the size
 * submitted here: the server conflicts only when the in-flight size differs (a matching one resolves
 * with the running run instead, which polls to success like any other), and on its query-failure path
 * the size is unknown. Once it finishes, refresh the balances and leave the customer to start a new
 * purchase.
 */
async function handleConflict(context: PurchaseRunContext, conflict: OrdwayPurchaseConflict) {
  const { config, rail, snackbars, queryClient } = context;
  snackbars.destroy(context.inProgressSnackbarId);

  // No run to poll. This attempt was refused outright, so name that failure first and point at the
  // balance — the running purchase may grant the credits — instead of inviting a retry that would
  // only conflict again.
  if (conflict.runId == null) {
    snackbars.create({
      variant: 'critical',
      id: `${config.snackbarPrefix}.purchase.error`,
      message: `Your purchase couldn't be started. Check your ${rail.balanceLabel}.`,
    });
    await invalidateCreditBalances(queryClient);
    return;
  }

  snackbars.create({
    variant: 'default',
    id: context.conflictSnackbarId,
    message: conflictInProgressMessage(rail, conflict.size),
    autoCloseTimeout: 0,
  });

  try {
    const { confirmed, error } = await pollToTerminalOutcome(context, conflict.runId);
    if (!confirmed) {
      if (error !== undefined) {
        // The poll's copy describes the caller's own purchase ("Your purchase is still processing…"),
        // which would be misleading here: this attempt was refused, so the run it waited on is someone
        // else's. Report that run in the third person and keep the specifics for Sentry.
        sentryCaptureException(error);
        snackbars.create({
          // Not the error variant: this attempt was refused rather than failed, and the run it was
          // watching may yet complete — the same reason an indeterminate outcome keeps the default.
          variant: 'default',
          id: `${config.snackbarPrefix}.purchase.error`,
          message: `The purchase already in progress hasn't completed. Check your ${rail.balanceLabel}.`,
        });
      }
      // Deliberately no `isChargeUnverified` here, unlike the direct path. That lock stops a re-click
      // from charging *the same* purchase twice; this attempt was refused, so nothing was charged for
      // it, and the run it waited on is a different size — a different purchase the customer may not
      // have made. Locking would refuse a sale on the strength of one they never submitted. The balance
      // refresh and the copy carry it instead.
      await invalidateCreditBalances(queryClient);
      return;
    }
    await invalidateCreditBalances(queryClient);
    snackbars.create({
      variant: 'default',
      id: `${config.snackbarPrefix}.purchase.conflictResolved`,
      message: `The purchase already in progress has finished. Check your ${rail.balanceLabel}.`,
    });
  } finally {
    snackbars.destroy(context.conflictSnackbarId);
  }
}

/**
 * Settles a purchase POST that threw for anything other than the 409 conflict. A request the server
 * refused establishes that nothing was charged; a response that was merely lost does not, so it
 * settles as the same unknown outcome a timed-out poll reports and locks buying with it.
 */
async function settleThrownPurchase(context: PurchaseRunContext, error: unknown) {
  sentryCaptureException(error);
  if (!ordwayPurchaseMayHaveStarted(error)) {
    reportFailure(context, 'ordway_error', refusedPurchaseError());
    return;
  }
  // No run id came back, so there is nothing to poll, and the server's duplicate guard lapses once
  // that purchase ends — a retry from here would charge again.
  reportFailure(context, 'ordway_error', ordwayPurchaseIndeterminateError(context.rail));
  await invalidateCreditBalances(context.queryClient);
}

/**
 * Settles a response that carries no `run_id`. The spec types it as required, but a services build
 * predating the workflow rollout resolves this endpoint synchronously and omits it — and that response
 * already settled the purchase, so its outcome is read rather than discarded.
 */
async function settleWithoutRun(context: PurchaseRunContext, response: unknown) {
  const outcome = parseLegacyOrdwayPurchaseOutcome(response);
  if (outcome === 'success') {
    reportSuccess(context);
    return;
  }
  if (outcome === 'failed') {
    // The synchronous endpoint orders, bills, *charges* and grants inside the request, so a terminal
    // `failed` from it can land after the card was collected. It carries the same unverified charge as
    // the workflow's `failed`, and locks buying the same way.
    reportFailure(context, 'legacy_failed', ordwayPurchaseFailedError());
    return;
  }
  reportFailure(
    context,
    'missing_run_id',
    reportIndeterminateOrdwayPurchase(
      context.rail,
      `Ordway ${context.rail.label} purchase resolved without a run id or status`,
    ),
  );
  await invalidateCreditBalances(context.queryClient);
}

/**
 * Whether the failure leaves open that the card was charged, which locks submit: the server's
 * duplicate guard only holds while the first workflow is running, so a re-click after it ended would
 * charge again. A declined card is the one outcome that provably collected nothing. Errors carrying no
 * verdict at all — the POST itself rejected before anything was created — leave buying open.
 */
function leavesChargeUnverified(failure: OrdwayPurchaseFailure | undefined): boolean {
  return failure === 'failed' || failure === 'indeterminate';
}

// The purchase errors carry their own copy (declined / failed / still processing); fall back to the
// generic failure only for errors that carry none.
function failureMessage(error: unknown): string {
  return error instanceof Error && !isEmptyString(error.message) ? error.message : FAILURE_MESSAGE;
}

/**
 * Copy for a request the server refused outright. Nothing was charged, but retrying the same request
 * will be refused the same way: every documented cause is a standing condition, not a blip — no card
 * on file, an ACH default the synchronous collection cannot use, or an environment with no Ordway
 * product configured. So this drops the generic copy's "try again", which no amount of retrying would
 * resolve. It points at the card because that is the cause a customer can act on — and at the payment
 * method generally, which is also where an ACH default shows up — then offers support for the rest
 * rather than asserting a diagnosis this client cannot make from a bare 400. Kept under the ~80-char
 * ceiling Pluma sets for a toast.
 */
function refusedPurchaseError(): Error {
  return new Error("Your purchase couldn't be started. Check your card on file or contact support.");
}

// Names the conflicting purchase by size when the server could read it from the running workflow.
function conflictInProgressSubject(rail: OrdwayPurchaseRail, size: number | undefined): string {
  return size == null ? 'Another purchase' : `A purchase of ${formatNumber(size)} ${plur(rail.sizeNoun, size)}`;
}

// The snackbar persists for the wait, so it carries that state without a second sentence — and
// without predicting an outcome this run does not control.
function conflictInProgressMessage(rail: OrdwayPurchaseRail, size: number | undefined): string {
  return `${conflictInProgressSubject(rail, size)} is already in progress.`;
}
